/**
 * Resilient localStorage facade.
 *
 * `window.localStorage` is `null` in Android WebViews with DOM storage disabled
 * (the WebView default) and can throw on access in some privacy modes / sandboxed
 * iframes. Direct access then crashes app boot (Sentry CUSTOMER-PORTAL-A — the
 * theme store at App `setup()`) and breaks every auth/axios call. This facade
 * falls back to an in-memory store so the app degrades gracefully: preferences
 * and the session simply don't persist across reloads instead of white-screening.
 *
 * `globalThis.localStorage` is read fresh on each call (not cached) so the real
 * store is used the moment it is available and test environments that reset or
 * replace it are honored. Every method is individually try/caught so a throwing
 * backing (quota, SecurityError) also falls back instead of propagating.
 */

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

let memory: Map<string, string> | null = null;

function memoryStore(): StorageLike {
  const map = (memory ??= new Map<string, string>());
  return {
    getItem: (key) => (map.has(key) ? (map.get(key) as string) : null),
    setItem: (key, value) => {
      map.set(key, String(value));
    },
    removeItem: (key) => {
      map.delete(key);
    },
  };
}

/** Real localStorage when reachable + non-null, else the in-memory fallback. */
function backing(): StorageLike {
  try {
    const ls = globalThis.localStorage;
    if (ls) return ls;
  } catch {
    // Accessing the property itself threw (sandboxed iframe / blocked storage).
  }
  return memoryStore();
}

export const safeStorage = {
  getItem(key: string): string | null {
    try {
      return backing().getItem(key);
    } catch {
      return memoryStore().getItem(key);
    }
  },
  setItem(key: string, value: string): void {
    try {
      backing().setItem(key, value);
    } catch {
      memoryStore().setItem(key, value);
    }
  },
  removeItem(key: string): void {
    try {
      backing().removeItem(key);
    } catch {
      memoryStore().removeItem(key);
    }
  },
};
